/** Matching LOCALE delle descrizioni gia presenti nelle dropdown. */
export type AIOptionField = {
  name: string;
  options?: unknown[] | (() => unknown[]);
  optionLabelKey?: string;
  optionValueKey?: string;
  onChange?: (...args: any[]) => void;
};

export type AIMatchResult = {
  value: string;
  label: string;
  score: number;
  automatic: boolean;
};

const STOP_WORDS = new Set([
  "progetto", "progetti", "attivita", "nodo", "nodi",
  "di", "del", "dello", "della", "dei", "degli", "delle",
  "a", "al", "allo", "alla", "ai", "agli", "alle",
  "da", "dal", "dalla", "con", "per", "su", "in", "nel", "nella",
  "il", "lo", "la", "i", "gli", "le", "un", "uno", "una", "e",
  "ore", "ora", "minuto", "minuti"
]);

export const normalizeAIText = (text: string): string =>
  String(text || "").toLowerCase().normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ").trim();

const tokens = (text: string): string[] =>
  normalizeAIText(text).split(" ").filter(Boolean);

const significantWords = (text: string): string[] =>
  tokens(text).filter(word =>
    word.length >= 3 && !STOP_WORDS.has(word) && !/^\d+$/.test(word)
  );

/** Conta le coppie consecutive di parole significative condivise.
 *  Es. "modello di controllo" -> ["modello", "controllo"]. */
function commonBigrams(query: string[], description: string[]): number {
  const queryPairs = new Set<string>();
  for (let i = 1; i < query.length; i += 1) {
    queryPairs.add(`${query[i - 1]}|${query[i]}`);
  }
  let count = 0;
  for (let i = 1; i < description.length; i += 1) {
    if (queryPairs.has(`${description[i - 1]}|${description[i]}`)) count += 1;
  }
  return count;
}

/** Riconosce una piccola differenza ortografica (es. distinte / distente). */
function oneEditApart(left: string, right: string): boolean {
  if (left.length < 6 || right.length < 6 ||
      Math.abs(left.length - right.length) > 1 || left === right) return false;

  let i = 0;
  let j = 0;
  let edits = 0;
  while (i < left.length && j < right.length) {
    if (left[i] === right[j]) {
      i += 1;
      j += 1;
      continue;
    }
    edits += 1;
    if (edits > 1) return false;
    if (left.length > right.length) i += 1;
    else if (right.length > left.length) j += 1;
    else { i += 1; j += 1; }
  }
  return edits + left.length - i + right.length - j === 1;
}

function scoreDescription(text: string, label: string): number {
  const query = significantWords(text);
  // Le sigle tecniche anteposte a un nodo (es. IMP06) non sono parole descrittive.
  const description = Array.from(new Set(significantWords(label).filter(
    word => !/^[a-z]{2,}\d+[a-z0-9]*$/.test(word)
  )));
  if (!query.length || !description.length) return 0;

  const normalizedText = normalizeAIText(text);
  const normalizedLabel = normalizeAIText(label);
  if (normalizedText === normalizedLabel) return 1;
  if (description.length >= 2 &&
      ` ${normalizedText} `.includes(` ${normalizedLabel} `)) return 0.99;

  const querySet = new Set(query);
  const common = description.filter(word => querySet.has(word));
  const coverage = common.length / description.length;
  const pairs = commonBigrams(query, description);

  // Le frasi consecutive rimangono il segnale piu forte.
  if (pairs > 0) {
    return Math.min(0.97,
      0.80 + 0.06 * Math.min(pairs, 3) +
      0.015 * Math.min(common.length, 5) + 0.02 * coverage
    );
  }

  // Due termini distintivi della descrizione valgono piu di una sola
  // parola lunga: "ECOMAR" + "FORMAZIONE" prevale su "FORMAZIONE" da sola.
  if (common.length >= 2 && coverage >= 0.25) {
    // Mantiene le sequenze di parole (bigrams) piu importanti: massimo 0.89.
    return Math.min(0.89, 0.85 + 0.05 * coverage +
      0.012 * Math.min(common.length, 4));
  }

  // Una parola distintiva puo identificare anche una descrizione lunga.
  // Esempio: "distinte" nel testo vs "IMP06 - Gestione distinte base".
  const longestExact = common.reduce((max, word) => Math.max(max, word.length), 0);
  if (longestExact >= 7) return Math.min(0.86, 0.72 + 0.012 * longestExact);
  if (description.length === 1 && common.some(word => word.length >= 4)) return 0.79;
  if (description.length <= 3 && common.some(word => word.length >= 6)) return 0.76;

  // Ultimo tentativo: singolo refuso o variazione finale, senza AI.
  const longestNear = description.reduce((max, word) =>
    word.length >= 7 && query.some(q => oneEditApart(q, word))
      ? Math.max(max, word.length)
      : max, 0
  );
  return longestNear ? Math.min(0.82, 0.75 + 0.007 * longestNear) : 0;
}

/** Riconosce sigle esplicite, solo se presenti in una singola descrizione. */
function findUniqueCode(
  text: string, options: Array<{value: string; label: string}>, fieldName: string
): string | null {
  const codes = new Set(text.match(/\b[A-Z][A-Z0-9]{2,}\b/g) || []);
  const matched = new Set<string>();
  codes.forEach(code => {
    // Evita che la sigla del progetto (es. GTG) forzi un nodo/attivita.
    if (fieldName !== "idprog" && !/\d/.test(code)) return;
    const candidates = options.filter(option =>
      tokens(option.label).includes(code.toLowerCase())
    );
    if (candidates.length === 1) matched.add(candidates[0].value);
  });
  return matched.size === 1 ? Array.from(matched)[0] : null;
}

/** Seleziona il punteggio piu alto; a parita prende il primo nell'ordine della dropdown. */
export function matchAIOption(
  fields: readonly AIOptionField[], fieldName: string, text: string
): AIMatchResult | null {
  const field = fields.find(item => item.name === fieldName);
  const raw = typeof field?.options === "function" ? field.options() : field?.options;
  const options = Array.isArray(raw) ? raw : [];
  const labelKey = field?.optionLabelKey ?? "label";
  const valueKey = field?.optionValueKey ?? "value";
  const candidates = options.map((item: any) => ({
    value: String(item?.[valueKey] ?? ""),
    label: String(item?.[labelKey] ?? "")
  })).filter(item => item.value && item.label);

  // Un codice esplicito e univoco prevale sul confronto descrittivo.
  const uniqueCode = findUniqueCode(text, candidates, fieldName);
  if (uniqueCode) {
    const selected = candidates.find(item => item.value === uniqueCode);
    if (selected) return {...selected, score: 1, automatic: true};
  }

  const ranked = candidates.map((item, index) => ({
    ...item,
    index,
    score: scoreDescription(text, item.label)
  })).sort((a, b) => b.score - a.score || a.index - b.index);

  const best = ranked[0];
  // Non basta essere il primo: deve esistere almeno una corrispondenza utile.
  if (!best || best.score < 0.75) return null;

  return {
    value: best.value,
    label: best.label,
    score: best.score,
    automatic: best.score >= 0.9
  };
}
